import { type BlogPost } from '../blogData'

const IMG = '/blog/gpt-vs-gemini-vs-heinrich'

export const phase1: BlogPost[] = [
  {
    slug: 'gpt-vs-gemini-vs-safety-ai',
    lang: 'ko',
    category: 'AI 벤치마크',
    title: 'GPT vs Gemini vs 산업안전 특화 AI — 같은 사진, 다른 분석',
    standfirst:
      '현장 사진 한 장, 동일 프롬프트. 세 AI에 똑같이 물었더니 결과가 완전히 달랐습니다. 개수가 아니라 "무엇이 따라오느냐"가 갈렸습니다.',
    date: '2026.05.20',
    hideDate: true,
    readMin: 7,
    cover: `${IMG}/page_01_cover.jpg`,
    featured: false,
    blocks: [
      { type: 'p', html: '안녕하세요, 산업안전 AI <strong>Heinrich</strong> 팀입니다. 오늘은 안전관리자분들이 한 번쯤 궁금해하실 실험 결과를 공유드립니다.' },
      { type: 'p', html: '현장 사진을 ChatGPT에 올려 위험요인 분석을 요청해 보신 적 있으신가요? 결과가 길게 나와 처음엔 만족했는데, 막상 현장에 적용하려니 <em>우선순위 판단</em>이 안 되셨던 경험 — 저희도 똑같이 했습니다. 그래서 같은 사진 한 장으로 GPT-5.4, Gemini 3.1 Pro, 산업안전 특화 AI 세 개를 동시에 비교했습니다.' },
      { type: 'img', src: `${IMG}/page_01_cover.jpg`, caption: '동일 사진 + 동일 프롬프트로 3개 AI 비교' },

      { type: 'h2', text: 'GPT-5.4 — 15개를 찾았지만' },
      { type: 'p', html: 'GPT-5.4는 무려 15개의 위험요인을 찾아냈습니다. 처음엔 "역시 GPT"였는데, 자세히 보니 이런 문제가 있었습니다.' },
      { type: 'ul', items: [
        '위험 등급 산정 없음 (전부 동일 비중)',
        '실제 사고 근거 인용 0건',
        '적용 법규(KOSHA·OSHA) 명시 없음',
        '위험 간 연쇄 분석 없음',
      ] },
      { type: 'p', html: '쉽게 말해 "발끝막이판 누락"처럼 <strong>추락 사망으로 직결되는 항목</strong>과 "느슨한 케이블"처럼 정리하면 되는 항목이 똑같은 한 줄로 처리됐습니다.' },
      { type: 'img', src: `${IMG}/page_02_gpt_result.jpg`, caption: 'GPT-5.4: 15개 검출 — 등급·근거·법규 모두 없음' },

      { type: 'h2', text: 'Gemini 3.1 Pro — 5개, 하지만 격리된 위험' },
      { type: 'p', html: '같은 사진을 Gemini 3.1 Pro에 넣자 5개가 나왔습니다. 개수는 줄었지만 똑똑해진 건 아니었습니다 — 위험요인별 개별 등급 없음, 실제 사고 매칭 0건, 연쇄고장 분석 전무. 모든 위험을 <em>독립적으로</em> 처리했습니다.' },
      { type: 'img', src: `${IMG}/page_03_gemini.jpg`, caption: 'Gemini 3.1 Pro: 5개 검출, 일반 매트릭스로 부분 점수화' },

      { type: 'h2', text: '산업안전 특화 AI — 4개, 그런데 전부 다르다' },
      { type: 'p', html: '같은 사진을 산업안전에 학습된 AI에 넣었더니 이번엔 4개가 나왔습니다. "이게 다인가?" 싶었지만 — 4개 모두 <strong>등급 + 근거 + 법규</strong>가 함께 따라왔습니다.' },
      { type: 'callout', label: '검출 결과 (등급·근거·법규 포함)', items: [
        '#1 [CRITICAL] 가드레일·경고테이프 결함 — 5/5 (사고사례 12건 매칭, OSHA 1926.502)',
        '#2 [CRITICAL] 안전대 미체결 — 5/5 (KOSHA G-26)',
        '#3 [HIGH] 비계 위 정리정돈 불량 — 4/5 (ISO 45001:6.1)',
        '#4 [MODERATE] 전동 공구·노출 케이블 — 3/5 (NFPA 70E)',
      ] },
      { type: 'p', html: '총 위험 점수 25/25 → "STOP WORK" 권고까지 자동으로 나옵니다.' },
      { type: 'img', src: `${IMG}/page_04_dasan_result.jpg`, caption: '산업안전 AI: 4개 모두 등급+근거+법규 포함' },

      { type: 'h2', text: '어떻게 등급을 매기나 — 5×5 위험성 매트릭스' },
      { type: 'p', html: 'Probability(발생확률) × Severity(심각도)로 위험요인별 1~25점이 산정됩니다. 20점 이상 STOP WORK / 15~19 CRITICAL / 10~14 HIGH / 5~9 MODERATE. GPT·Gemini는 매트릭스가 없어 "이 위험이 즉시 작업중지 사유인지" 판단할 근거가 부족합니다.' },
      { type: 'img', src: `${IMG}/page_05_matrix.jpg`, caption: '5×5 위험성 매트릭스 (P × S)' },

      { type: 'h2', text: '진짜 위험은 "체인"이다' },
      { type: 'p', html: '실제 사고는 단일 원인으로 일어나지 않습니다 — 연쇄로 일어납니다. <em>흩어진 공구 → 가장자리 비틀거림 → 가드레일 부재 → 안전대 미체결 → 치명적 추락.</em> GPT는 15개를 별개로, Gemini도 격리된 위험으로 봤지만, 산업안전 특화 AI만 이 체인을 매핑해 "이 4개가 합쳐지면 사망 시퀀스가 된다"고 명시했습니다.' },
      { type: 'pullquote', text: '"많이 찾는 것"은 분석이 아니라 소음입니다. 안전관리자에게 필요한 건 우선순위가 매겨진 4개입니다.' },
      { type: 'img', src: `${IMG}/page_06_chain.jpg`, caption: '4개 위험요인이 결합되는 사망 시퀀스' },

      { type: 'h2', text: '16건의 법규 자동 인용' },
      { type: 'p', html: '산업안전 특화 AI는 모든 위험요인에 감사관이 실제로 확인하는 조항을 자동 인용합니다 — OSHA 1926.502(가드레일)·1910.147(LOTO), KOSHA G-26(안전대)·산업안전보건법 §38, ISO 45001:6.1, NFPA 70E. GPT·Gemini는 법규 인용 0건이라, 감사 때 "이거 어디서 나온 답이에요?"에 답할 수 없습니다.' },
      { type: 'img', src: `${IMG}/page_07_citations.jpg`, caption: 'OSHA·KOSHA·ISO 16건 자동 인용' },

      { type: 'h2', text: '왜 이런 차이가 날까 — 학습 데이터' },
      { type: 'p', html: '일반 LLM(GPT·Gemini)은 위키·뉴스·일반 문서 위주로 학습해 산업안전 도메인 비중이 매우 낮고, 한국 산업안전보건법·KOSHA 가이드는 거의 없습니다. 반면 산업안전 특화 AI는 <strong>270만+ 라벨링 사고사례</strong>와 안전법규·KOSHA 가이드를 높은 비중으로 학습하고, 위험 검출 시 치명도를 자동 산정하도록 훈련됐습니다.' },
      { type: 'img', src: `${IMG}/page_08_conclusion.jpg`, caption: '결론: 분석이 아니라 소음' },

      { type: 'h2', text: '정리' },
      { type: 'ul', items: [
        'GPT 15개 / Gemini 5개 / 산업안전 AI 4개 — 개수는 의미 없습니다.',
        '산업안전 AI는 등급(P×S 매트릭스) + 사고사례 매칭 + 법규 인용 16건 + 체인 매핑까지 자동.',
        '필요한 건 "많이"가 아니라 "우선순위가 매겨진" 위험요인입니다.',
      ] },

      { type: 'cta',
        title: '사진 한 장으로 직접 비교해 보세요',
        body: '현장 사진을 올리면 위험요인이 등급·근거·법규와 함께 나옵니다. 무료로 시작, 카드 등록 없이 가입 30초.',
        label: 'Heinrich 무료로 시작하기 →',
        href: '/signup' },
    ],
  },
  {
    slug: 'gpt-vs-gemini-vs-safety-ai',
    lang: 'en',
    category: 'AI Benchmark',
    title: 'GPT vs Gemini vs Safety-Trained AI — Same Photo, Different Analysis',
    standfirst:
      'One site photo, one prompt, three AIs. The results diverged completely — and the difference was not the count, but what came attached to each hazard.',
    date: '2026.05.20',
    hideDate: true,
    readMin: 7,
    cover: `${IMG}/page_01_cover.jpg`,
    featured: false,
    blocks: [
      { type: 'p', html: 'Have you ever uploaded a site photo to ChatGPT and asked it to analyze the hazards? The answer comes back long, and at first it looks impressive — until you try to act on it and realize you cannot <em>prioritize</em>. We had the same experience. So we ran the same photo through GPT-5.4, Gemini 3.1 Pro, and a safety-trained AI at once.' },
      { type: 'img', src: `${IMG}/page_01_cover.jpg`, caption: 'Same photo + same prompt, compared across three AIs' },

      { type: 'h2', text: 'GPT-5.4 — 15 hazards, but' },
      { type: 'p', html: 'GPT-5.4 found 15 hazards. "Classic GPT." Then we looked closer:' },
      { type: 'ul', items: [
        'No risk rating (every item weighted the same)',
        'Zero references to real accidents',
        'No applicable regulations cited',
        'No chain-of-failure analysis',
      ] },
      { type: 'p', html: 'In other words, a <strong>missing toe-board that leads directly to a fatal fall</strong> and a "loose cable you can just tidy up" were treated as the same one-line item.' },
      { type: 'img', src: `${IMG}/page_02_gpt_result.jpg`, caption: 'GPT-5.4: 15 hazards — no rating, no basis, no regulation' },

      { type: 'h2', text: 'Gemini 3.1 Pro — 5, but each isolated' },
      { type: 'p', html: 'The same photo through Gemini 3.1 Pro returned 5. Fewer, but not smarter — no per-hazard rating, zero accident matching, no cascade analysis. Every hazard treated <em>independently</em>.' },
      { type: 'img', src: `${IMG}/page_03_gemini.jpg`, caption: 'Gemini 3.1 Pro: 5 detected, partial generic scoring' },

      { type: 'h2', text: 'Safety-trained AI — 4, but all different' },
      { type: 'p', html: 'The same photo through an AI trained on industrial safety returned 4. "Is that all?" — but all 4 came with a <strong>rating + basis + regulation</strong>.' },
      { type: 'callout', label: 'Detection result (rating · basis · regulation)', items: [
        '#1 [CRITICAL] Guardrail / warning-tape defect — 5/5 (12 accident matches, OSHA 1926.502)',
        '#2 [CRITICAL] Fall-arrest not clipped — 5/5 (KOSHA G-26)',
        '#3 [HIGH] Poor housekeeping on scaffold — 4/5 (ISO 45001:6.1)',
        '#4 [MODERATE] Power tools / exposed cable — 3/5 (NFPA 70E)',
      ] },
      { type: 'p', html: 'Total risk score 25/25 → an automatic "STOP WORK" recommendation.' },
      { type: 'img', src: `${IMG}/page_04_dasan_result.jpg`, caption: 'Safety AI: 4 hazards, all with rating + basis + regulation' },

      { type: 'h2', text: 'How the rating works — a 5×5 risk matrix' },
      { type: 'p', html: 'Probability × Severity yields a 1–25 score per hazard. 20+ STOP WORK / 15–19 CRITICAL / 10–14 HIGH / 5–9 MODERATE. GPT and Gemini have no matrix, so there is no basis to decide whether a hazard is an immediate stop-work trigger.' },
      { type: 'img', src: `${IMG}/page_05_matrix.jpg`, caption: '5×5 risk matrix (P × S)' },

      { type: 'h2', text: 'The real risk is the "chain"' },
      { type: 'p', html: 'Real accidents rarely have a single cause — they cascade. <em>Scattered tools → stumble at the edge → no guardrail → fall-arrest unclipped → fatal fall.</em> GPT saw 15 separate items, Gemini saw isolated hazards, but only the safety-trained AI mapped the chain: "these four combined become a fatality sequence."' },
      { type: 'pullquote', text: '"Finding more" is not analysis — it is noise. What a safety manager needs is four hazards, prioritized.' },
      { type: 'img', src: `${IMG}/page_06_chain.jpg`, caption: 'Four hazards combining into a fatality sequence' },

      { type: 'h2', text: '16 regulations, auto-cited' },
      { type: 'p', html: 'The safety-trained AI cites, for every hazard, the exact clause an auditor actually checks — OSHA 1926.502 (guardrails)·1910.147 (LOTO), KOSHA G-26, ISO 45001:6.1, NFPA 70E. GPT and Gemini cite zero, so when an auditor asks "where did this come from?", there is no answer.' },
      { type: 'img', src: `${IMG}/page_07_citations.jpg`, caption: '16 citations across OSHA · KOSHA · ISO' },

      { type: 'h2', text: 'Why the difference — training data' },
      { type: 'p', html: 'General LLMs (GPT, Gemini) train mostly on wikis, news and generic documents, so their industrial-safety weight is very low. A safety-trained AI, by contrast, learns from <strong>2.7M+ labeled accident records</strong> plus safety regulations and standards, and is trained to score severity automatically at detection time.' },
      { type: 'img', src: `${IMG}/page_08_conclusion.jpg`, caption: 'Conclusion: noise, not analysis' },

      { type: 'h2', text: 'The takeaway' },
      { type: 'ul', items: [
        'GPT 15 / Gemini 5 / Safety AI 4 — the count is meaningless.',
        'Safety AI adds rating (P×S matrix) + accident matching + 16 citations + chain mapping, automatically.',
        'You do not need "more" — you need hazards that are prioritized.',
      ] },

      { type: 'cta',
        title: 'Compare it yourself with one photo',
        body: 'Upload a site photo and get hazards back with rating, basis and regulation. Free to start, no card required.',
        label: 'Start Heinrich free →',
        href: '/signup' },
    ],
  },
]
